/**
 * `useBridgeState(namespace, path)` — typed primitive backed by
 * `useSyncExternalStore` against `bridgeStateCache`. Wave 3 (plan 06)
 * implements optimistic writes + reconciliation + cache dedup.
 */
import { useSyncExternalStore, useCallback } from 'react'
import { bridgeStateCache } from './bridgeStateCache'

export function useBridgeState<T = unknown>(
  namespace: string,
  path: string,
): [T | undefined, (value: T) => void] {
  const subscribe = useCallback(
    (listener: () => void) => bridgeStateCache.subscribe(namespace, listener),
    [namespace],
  )
  const getSnapshot = useCallback(
    () => bridgeStateCache.getSnapshot(namespace, path) as T | undefined,
    [namespace, path],
  )
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const setValue = useCallback(
    (next: T) => bridgeStateCache.write(namespace, path, next),
    [namespace, path],
  )
  return [value, setValue]
}
